import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { memo } from 'react';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';

import AdContainer from '@/components/Ads/AdContainer';
import CompleteAccountBanner from '@/components/CompleteAccountBanner/CompleteAccountBanner';
import DatePickerFacet from '@/components/Facets/DatePickerFacet';
import MultiCheckFacet from '@/components/Facets/MultiCheckFacet';
import { SearchFacetValuesOptions } from '@/redux/modules/auctionCalendar/auctionCalendar.api';
import FacetTypes from '@/types/search/enums/facetTypes';
import { useAverageLotValueActionCalendarRanking } from '@/utils/amplitude/experiments/hooks/useAverageLotValueActionCalendarRanking';

import AuctionCalendarPageHeader from './AuctionCalendarPageHeader';
import AuctionCalendarPageHeaderMeta from './AuctionCalendarPageHeaderMeta';
import AuctionCalendarResults from './AuctionCalendarResults';

const emptyAHMessage = (
    <FormattedMessage
        id="search_no_auction_house_found"
        key="no_ah_found"
    />
);
const emptyTypeMessage = (
    <FormattedMessage
        id="search_no_types_found"
        key="no_ah_found"
    />
);
const emptyCountryMessage = (
    <FormattedMessage
        id="search_no_country_found"
        key="no_ah_found"
    />
);
const emptyLiveBroadcastMessage = (
    <FormattedMessage
        id="search_no_audio_video_found"
        key="no_ah_found"
    />
);
const emptyShippingOptionsMessage = (
    <FormattedMessage
        id="search_no_shipping_options_found"
        key="no_ah_found"
    />
);

type Props = {
    auctionHouses: SearchFacetValuesOptions[];
    baseUrl: string;
    catalogIds: number[];
    catalogsTodayCount: number;
    changePageAndNavigate: Function;
    changePageSizeAndNavigate: Function;
    countries: SearchFacetValuesOptions[];
    dateChanged: Function;
    facetOptionChecked: (facetName: string, value: string, isChecked: boolean) => void;
    liveBroadcastOptions: SearchFacetValuesOptions[];
    page: number;
    pageSize: number;
    pathname: string;
    queryParams: any;
    saleTypes: SearchFacetValuesOptions[];
    searchSubmitted: boolean;
    selectedDate: string;
    shippingOptions: SearchFacetValuesOptions[];
    topRatedAuctionHouses: SearchFacetValuesOptions[];
    totalCatalogsFound: number;
    whiteLabel: boolean;
};

const AuctionCalendarPage = ({
    auctionHouses,
    baseUrl,
    catalogIds,
    catalogsTodayCount,
    changePageAndNavigate,
    changePageSizeAndNavigate,
    countries,
    dateChanged,
    facetOptionChecked,
    liveBroadcastOptions,
    page,
    pageSize,
    pathname,
    queryParams,
    saleTypes,
    searchSubmitted,
    selectedDate,
    shippingOptions,
    topRatedAuctionHouses,
    totalCatalogsFound,
    whiteLabel,
}: Props) => {
    const { formatMessage } = useIntl();
    useAverageLotValueActionCalendarRanking();

    return (
        <StyledAuctionCalanderContainer>
            <AuctionCalendarPageHeaderMeta
                baseUrl={baseUrl}
                catalogsTodayCount={catalogsTodayCount}
                page={page}
            />
            <CompleteAccountBanner />
            <AuctionCalendarPageHeader
                queryParams={queryParams}
                totalFound={totalCatalogsFound}
            />
            <StyledUpcomingWrapper>
                <PageSearchWrapper>
                    <StyledPageSearchItems>
                        <StyledFacetsContainer>
                            <ShowResults />
                            <DatePickerFacet
                                onDateChange={dateChanged}
                                selectedDate={selectedDate}
                            />
                            <MultiCheckFacet
                                checklist={saleTypes}
                                emptyMessage={emptyTypeMessage}
                                facetId={FacetTypes.SALE_TYPE_ID}
                                facetOptionChecked={facetOptionChecked}
                                queryParams={queryParams}
                                showSearchHeader={false}
                                title={formatMessage({
                                    id: 'search_facet_title_sale_type',
                                })}
                            />
                            {topRatedAuctionHouses.length > 0 && !whiteLabel ? (
                                <MultiCheckFacet
                                    checklist={topRatedAuctionHouses}
                                    emptyMessage={emptyTypeMessage}
                                    facetId={FacetTypes.TOP_RATED_ID}
                                    facetOptionChecked={facetOptionChecked}
                                    queryParams={queryParams}
                                    showSearchHeader={false}
                                    title={formatMessage({
                                        id: 'auctionCalendar.houseRating',
                                    })}
                                />
                            ) : null}
                            <MultiCheckFacet
                                checklist={shippingOptions}
                                emptyMessage={emptyShippingOptionsMessage}
                                facetId={FacetTypes.SHIPPING}
                                facetOptionChecked={facetOptionChecked}
                                queryParams={queryParams}
                                showSearchHeader={false}
                                title={formatMessage({
                                    id: 'auctionCalendar.shippingOptions',
                                })}
                            />
                            <MultiCheckFacet
                                checklist={liveBroadcastOptions}
                                emptyMessage={emptyLiveBroadcastMessage}
                                facetId={FacetTypes.AUDIO_VIDEO}
                                facetOptionChecked={facetOptionChecked}
                                queryParams={queryParams}
                                showSearchHeader={false}
                                title={formatMessage({
                                    id: 'auctionCalendar.liveBroadcast',
                                })}
                            />
                            {!whiteLabel && (
                                <MultiCheckFacet
                                    checklist={auctionHouses}
                                    emptyMessage={emptyAHMessage}
                                    facetId={FacetTypes.AUCTION_HOUSE_ID}
                                    facetOptionChecked={facetOptionChecked}
                                    queryParams={queryParams}
                                    searchPlaceholder={formatMessage({
                                        id: 'searchByHousePlaceholder',
                                    })}
                                    showSearchHeader
                                    title={formatMessage({
                                        id: 'auction_house',
                                    })}
                                />
                            )}
                            <MultiCheckFacet
                                checklist={countries}
                                emptyMessage={emptyCountryMessage}
                                facetId={FacetTypes.COUNTRY_CODE_ID}
                                facetOptionChecked={facetOptionChecked}
                                queryParams={queryParams}
                                showSearchHeader={false}
                                title={formatMessage({
                                    id: 'countryCapitalized',
                                })}
                            />
                            {!whiteLabel && (
                                <AdContainer
                                    pageName="calendar"
                                    pathname={pathname}
                                    queryParams={queryParams}
                                />
                            )}
                        </StyledFacetsContainer>

                        <SearchPageResultsWrapper>
                            <AuctionCalendarResults
                                catalogIds={catalogIds}
                                changePageAndNavigate={changePageAndNavigate}
                                changePageSizeAndNavigate={changePageSizeAndNavigate}
                                page={page}
                                pageSize={pageSize}
                                totalItemsFound={totalCatalogsFound}
                            />
                        </SearchPageResultsWrapper>
                        {searchSubmitted && <LoadingThrobber large />}
                    </StyledPageSearchItems>
                </PageSearchWrapper>
            </StyledUpcomingWrapper>
        </StyledAuctionCalanderContainer>
    );
};

export default memo<Props>(AuctionCalendarPage, isEqual);

const StyledAuctionCalanderContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const StyledUpcomingWrapper = styled.div`
    padding: 0 0 21px;
    position: relative;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    margin-bottom: 30px;
    max-width: 1200px;
    align-self: center;
    width: calc(100% - 40px);
`;

const PageSearchWrapper = styled.div`
    display: flex;
    position: relative;
    flex-direction: column;
    min-height: 400px;
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    margin: 0 auto;
    width: 100%;

    @media (max-width: 1200px) {
        margin-left: 0;
        min-width: 55%;
        max-width: 100%;
        min-height: 300px;
    }

    @media (max-width: 670px) {
        padding: 0;
    }
`;

const StyledPageSearchItems = styled.div`
    display: flex;
    flex-direction: row;
`;

const StyledFacetsContainer = styled.div`
    margin: -30px 20px 0 20px;
    z-index: 3;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        flex: 0 0 10%;
        min-width: 200px;
        display: block;
        margin: -20px 28px 0 0;
    }

    @media (min-width: 1024px) {
        flex: 0 0 10%;
        min-width: 200px;
        display: block;
        margin: 0 36px 0 0;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        display: none;
    }
`;

const ShowResults = styled.div`
    text-transform: uppercase;
    font-size: 16px;
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    font-weight: 400;
    font-style: normal;
    font-stretch: normal;
    margin-bottom: 19px;
    line-height: 13px;
    letter-spacing: 1px;
    color: ${({ theme }) => theme.colors.grey100};
`;

const SearchPageResultsWrapper = styled.div`
    width: 100%;
`;

const LoadingThrobber = styled(Throbber)`
    position: absolute;
    right: 0;
    width: 100%;
    height: 100%;
    background-color: ${({ theme }) => theme.colors.shadowOverlay50};
    margin-top: 0;

    @media (min-width: 375px) {
        padding: 100px 0 0;
    }

    @media (min-width: 976px) {
        padding: 200px 0 0;
    }
`;
